/**
 * GoalsHub — unified Goals page with Mine / Team / Org tabs.
 *
 * Consolidates `My Goals` (/goals) and `Team Goals` (/team-goals) into a
 * single sidebar entry. Org tab shows a roll-up of all published goals
 * across the workspace for HR roles. Tab is driven by ?scope= so deep-links
 * and the legacy /team-goals redirect both land users on the right pane.
 *
 * The hub renders only a tab strip; each pane keeps its own
 * cycle-aware header to preserve existing UX (cycle name, status pills, etc.).
 */
import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Target, Users, Building2 } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth, hasRole } from "@/lib/auth";
import Goals from "@/pages/Goals";
import TeamGoals from "@/pages/TeamGoals";
import OrgGoalsPane from "@/pages/OrgGoalsPane";

const TABS = [
  { id: "mine", label: "Mine", icon: Target, roles: null },
  { id: "team", label: "Team", icon: Users, roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp", "manager"] },
  { id: "org", label: "Org", icon: Building2, roles: ["super_admin", "tenant_admin", "hr_admin", "hrbp"] },
];

export default function GoalsHub() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const requested = params.get("scope") || "mine";
  const visible = TABS.filter((t) => !t.roles || hasRole(user, ...t.roles));
  const active = visible.find((t) => t.id === requested) ? requested : "mine";

  const setTab = (id) => {
    const next = new URLSearchParams(location.search);
    if (id === "mine") next.delete("scope");
    else next.set("scope", id);
    navigate({ pathname: "/goals", search: next.toString() ? `?${next.toString()}` : "" }, { replace: true });
  };

  return (
    <div className="space-y-5" data-testid="goals-hub">
      {visible.length > 1 && (
        <nav className="flex items-center gap-1 border-b border-slate-200" data-testid="goals-tabs">
          {visible.map((t) => {
            const Icon = t.icon;
            const isActive = t.id === active;
            return (
              <button
                key={t.id}
                type="button"
                onClick={() => setTab(t.id)}
                data-testid={`goals-tab-${t.id}`}
                className={cn(
                  "inline-flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition -mb-[2px]",
                  isActive
                    ? "border-slate-900 text-slate-900"
                    : "border-transparent text-slate-500 hover:text-slate-900",
                )}
              >
                <Icon className="w-3.5 h-3.5" />
                {t.label}
              </button>
            );
          })}
        </nav>
      )}

      <div data-testid={`goals-pane-${active}`}>
        {active === "mine" && <Goals />}
        {active === "team" && <TeamGoals />}
        {active === "org" && <OrgGoalsPane />}
      </div>
    </div>
  );
}
